// Etapas do curso: ordem com subir/descer, remoção e inclusão no fim.
// Mover e remover ficam num rascunho salvo de uma vez (writeBatch), porque o bot lê
// a sequência a cada minuto e alunos em andamento dependem da posição das etapas.
import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, doc, writeBatch } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FiAlertTriangle, FiArrowDown, FiArrowUp, FiImage, FiPlus, FiRotateCcw, FiTrash2, FiUsers } from 'react-icons/fi';
import { firestore } from '../../config/firebaseConfig';
import AdicionarEtapa, { IconeTipo } from './AdicionarEtapa';
import Dialogo from './Dialogo';
import { mensagensDaPilula } from './mensagensBot';
import {
  alunosAfetados, alunosPorPosicao, ehQuiz, plural, problemasDoQuiz, respostaCorreta, rotuloEtapa, tituloEtapa,
} from './util';

// ordem: ids na nova ordem; removidas: ids a apagar; movidas: ids que a pessoa moveu (destaque)
export const RASCUNHO_VAZIO = { ordem: null, removidas: [], movidas: [] };

// Aplica o rascunho sobre as etapas salvas; etapas novas (ou vindas de fora) entram no fim
export function aplicarRascunho(salvas, rascunho) {
  const porId = Object.fromEntries(salvas.map((e) => [e.id, e]));
  const ids = rascunho.ordem ? rascunho.ordem.filter((id) => porId[id]) : salvas.map((e) => e.id);
  if (rascunho.ordem) salvas.forEach((e) => { if (!ids.includes(e.id)) ids.push(e.id); });
  const removidas = new Set(rascunho.removidas.filter((id) => porId[id]));
  return {
    todas: ids.map((id) => porId[id]),
    ativas: ids.filter((id) => !removidas.has(id)).map((id) => porId[id]),
    removidas,
  };
}

const numeracaoContinua = (salvas) => salvas.every((e, i) => Number(e.ordem) === i + 1);

function EtapasCurso({
  curso, tenantId, salvas, rascunho, setRascunho, andamento, temaCursoId,
  pilulas, questoes, temas, lateral,
}) {
  const [adicionarAberto, setAdicionarAberto] = useState(salvas.length === 0);
  const [confirmando, setConfirmando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [focoPendente, setFocoPendente] = useState(null);
  const [anuncio, setAnuncio] = useState('');

  const { todas, ativas, removidas } = useMemo(() => aplicarRascunho(salvas, rascunho), [salvas, rascunho]);
  const idsSalvos = useMemo(() => salvas.map((e) => e.id), [salvas]);
  const idsNovos = useMemo(() => ativas.map((e) => e.id), [ativas]);
  const posicaoSalva = useMemo(() => Object.fromEntries(idsSalvos.map((id, i) => [id, i + 1])), [idsSalvos]);
  const baseSemRemovidas = useMemo(() => idsSalvos.filter((id) => !removidas.has(id)), [idsSalvos, removidas]);
  const mudouOrdem = idsNovos.some((id, i) => baseSemRemovidas[i] !== id);
  const sujo = removidas.size > 0 || mudouOrdem;
  const corrigirNumeracao = !sujo && !numeracaoContinua(salvas);
  const afetados = useMemo(() => alunosAfetados(andamento, idsSalvos, idsNovos), [andamento, idsSalvos, idsNovos]);
  const porPosicao = useMemo(() => alunosPorPosicao(andamento), [andamento]);
  const idsNoCurso = useMemo(() => new Set(salvas.map((e) => e.conteudoId)), [salvas]);

  // mantém o foco no botão da etapa que acabou de mudar de lugar
  useEffect(() => {
    if (!focoPendente) return;
    const { id, acao } = focoPendente;
    const alvo = document.getElementById(`mc-etapa-${id}-${acao}`);
    const outro = document.getElementById(`mc-etapa-${id}-${acao === 'subir' ? 'descer' : 'subir'}`);
    if (alvo && !alvo.disabled) alvo.focus();
    else if (outro) outro.focus();
    setFocoPendente(null);
  }, [focoPendente, todas]);

  const ordemAtual = () => todas.map((e) => e.id);

  const mover = (id, direcao) => {
    const ids = ordemAtual();
    const de = ids.indexOf(id);
    let para = de + direcao;
    while (para >= 0 && para < ids.length && removidas.has(ids[para])) para += direcao;
    if (para < 0 || para >= ids.length) return;
    ids.splice(de, 1);
    ids.splice(para, 0, id);
    setRascunho((r) => ({ ...r, ordem: ids, movidas: r.movidas.includes(id) ? r.movidas : [...r.movidas, id] }));
    const novaPosicao = ids.filter((x) => !removidas.has(x)).indexOf(id) + 1;
    setAnuncio(`Etapa movida para a posição ${novaPosicao} de ${idsNovos.length}.`);
    setFocoPendente({ id, acao: direcao < 0 ? 'subir' : 'descer' });
  };

  const remover = (etapa) => {
    setRascunho((r) => ({ ...r, ordem: r.ordem || ordemAtual(), removidas: [...r.removidas, etapa.id] }));
    setAnuncio(`Etapa "${tituloEtapa(etapa)}" marcada para remover. Salve a sequência para confirmar.`);
    setFocoPendente({ id: etapa.id, acao: 'desfazer' });
  };

  const desfazer = (etapa) => {
    setRascunho((r) => ({ ...r, removidas: r.removidas.filter((id) => id !== etapa.id) }));
    setAnuncio('Remoção desfeita.');
    setFocoPendente({ id: etapa.id, acao: 'remover' });
  };

  const descartar = () => {
    setRascunho(RASCUNHO_VAZIO);
    setAnuncio('Alterações descartadas.');
  };

  const gravar = async () => {
    setSalvando(true);
    try {
      const batch = writeBatch(firestore);
      removidas.forEach((id) => batch.delete(doc(firestore, 'etapasMinicurso', id)));
      ativas.forEach((e, i) => {
        if (Number(e.ordem) !== i + 1) batch.update(doc(firestore, 'etapasMinicurso', e.id), { ordem: i + 1 });
      });
      await batch.commit();
      setRascunho(RASCUNHO_VAZIO);
      setConfirmando(false);
      toast.success(removidas.size ? 'Sequência salva e etapas removidas.' : 'Sequência salva.');
    } catch (erro) {
      console.error('Erro ao salvar a sequência:', erro);
      toast.error('Não foi possível salvar a sequência. Nada foi alterado.');
    } finally {
      setSalvando(false);
    }
  };

  const pedirParaSalvar = () => {
    if (removidas.size || afetados.length) setConfirmando(true);
    else gravar();
  };

  const adicionar = async (tipo, item) => {
    const maior = salvas.reduce((m, e) => Math.max(m, Number(e.ordem) || 0), salvas.length);
    try {
      await addDoc(collection(firestore, 'etapasMinicurso'), {
        minicursoId: curso.id,
        ordem: maior + 1,
        tipoConteudo: tipo,
        conteudoId: item.id,
        tenantId,
      });
      toast.success(`Etapa ${ativas.length + 1} adicionada no fim do curso.`);
    } catch (erro) {
      console.error('Erro ao adicionar etapa:', erro);
      toast.error('Não foi possível adicionar a etapa.');
    }
  };

  const lista = (
    <div className="mc-etapas">
      {andamento.length > 0 && (
        <p className="mc-alerta mc-alerta--info">
          <FiUsers aria-hidden="true" />
          <span>
            <strong>
              {curso.status === 'ativo'
                ? `${plural(andamento.length, 'aluno está', 'alunos estão')} fazendo este curso agora.`
                : `${plural(andamento.length, 'aluno em andamento, pausado', 'alunos em andamento, pausados')} enquanto o curso está inativo.`}
            </strong>{' '}
            Mover ou remover etapas que eles já receberam faz com que pulem ou repitam conteúdo.
            Adicionar no fim é seguro. A posição de cada um aparece nas etapas.
          </span>
        </p>
      )}

      {todas.length === 0 ? (
        <div className="qz-empty mc-vazio">
          <p>Este curso ainda não tem etapas.</p>
          <p className="mc-suave">Adicione pílulas e questões; a primeira etapa vai logo depois do comando.</p>
        </div>
      ) : (
        <ol className="mc-etapas__lista" aria-label="Etapas do curso, na ordem de envio">
          {todas.map((etapa) => {
            const removida = removidas.has(etapa.id);
            const indice = idsNovos.indexOf(etapa.id);
            const n = indice + 1;
            const antes = posicaoSalva[etapa.id];
            const movida = !removida && mudouOrdem && rascunho.movidas.includes(etapa.id) && baseSemRemovidas.indexOf(etapa.id) !== indice;
            const titulo = tituloEtapa(etapa);
            const aqui = removida ? 0 : porPosicao[antes - 1] || 0;
            const problema = etapa.conteudo === null
              ? { nivel: 'perigo', curto: 'Conteúdo apagado' }
              : (etapa.conteudo && ehQuiz(etapa) ? problemasDoQuiz(etapa.conteudo) : null);
            const correta = etapa.conteudo && ehQuiz(etapa) ? respostaCorreta(etapa.conteudo.options).letra : null;
            const temImagem = etapa.tipoConteudo === 'pilula' && etapa.conteudo && mensagensDaPilula(etapa.conteudo)[0].imagem;
            const primeira = indice === 0;
            const ultima = indice === idsNovos.length - 1;
            return (
              <li
                key={etapa.id}
                className={`mc-etapa${removida ? ' is-removida' : ''}${movida ? ' is-movida' : ''}${problema?.nivel === 'perigo' ? ' is-problema' : ''}`}
              >
                <span className="mc-etapa__n" aria-hidden="true">{removida ? '–' : n}</span>
                <span className={`mc-etapa__icone mc-etapa__icone--${etapa.tipoConteudo === 'pilula' ? 'pilula' : 'questao'}`}>
                  <IconeTipo etapa={etapa} />
                </span>
                <div className="mc-etapa__corpo">
                  <div className="mc-etapa__meta">
                    <span className="mc-oculto">{removida ? 'Será removida: ' : `Etapa ${n}: `}</span>
                    <span className="mc-etapa__tipo">{rotuloEtapa(etapa)}</span>
                    {correta && !problema && <span className="mc-chip">correta: {correta}</span>}
                    {temImagem && <span className="mc-chip"><FiImage aria-hidden="true" /> imagem</span>}
                    {problema && (
                      <span className={`qz-badge ${problema.nivel === 'perigo' ? 'qz-badge--danger' : 'qz-badge--warning'}`}>
                        <FiAlertTriangle aria-hidden="true" /> {problema.curto}
                      </span>
                    )}
                    {movida && <span className="mc-chip mc-chip--destaque">antes: {antes}</span>}
                    {removida && <span className="qz-badge qz-badge--danger">será removida</span>}
                    {aqui > 0 && (
                      <span className="mc-chip mc-chip--alunos" title="Alunos respondendo esta etapa ou prestes a recebê-la">
                        <FiUsers aria-hidden="true" /> {plural(aqui, 'aluno aqui', 'alunos aqui')}
                      </span>
                    )}
                  </div>
                  <p className="mc-etapa__titulo">{titulo}</p>
                </div>
                <div className="mc-etapa__acoes">
                  {removida ? (
                    <button
                      type="button"
                      id={`mc-etapa-${etapa.id}-desfazer`}
                      className="btn-secondary mc-botao mc-botao--pequeno"
                      onClick={() => desfazer(etapa)}
                      aria-label={`Desfazer remoção de ${titulo}`}
                    >
                      <FiRotateCcw aria-hidden="true" /> Desfazer
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        id={`mc-etapa-${etapa.id}-subir`}
                        className="btn-ghost mc-icone-botao"
                        onClick={() => mover(etapa.id, -1)}
                        disabled={primeira}
                        aria-label={`Subir etapa ${n}: ${titulo}`}
                        title="Subir"
                      >
                        <FiArrowUp aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        id={`mc-etapa-${etapa.id}-descer`}
                        className="btn-ghost mc-icone-botao"
                        onClick={() => mover(etapa.id, 1)}
                        disabled={ultima}
                        aria-label={`Descer etapa ${n}: ${titulo}`}
                        title="Descer"
                      >
                        <FiArrowDown aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        id={`mc-etapa-${etapa.id}-remover`}
                        className="btn-ghost mc-icone-botao mc-icone-botao--perigo"
                        onClick={() => remover(etapa)}
                        aria-label={`Remover etapa ${n}: ${titulo}`}
                        title="Remover"
                      >
                        <FiTrash2 aria-hidden="true" />
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <p className="mc-oculto" aria-live="polite">{anuncio}</p>

      {(sujo || corrigirNumeracao) && (
        <div className={`mc-barra-salvar${afetados.length ? ' is-alerta' : ''}`} role="region" aria-label="Alterações na sequência">
          <div className="mc-barra-salvar__texto">
            {sujo ? (
              <strong>
                {[mudouOrdem && 'Ordem alterada', removidas.size && plural(removidas.size, 'etapa será removida', 'etapas serão removidas')]
                  .filter(Boolean).join(' · ')}
              </strong>
            ) : (
              <strong>A numeração salva tem falhas ou repetições.</strong>
            )}
            <span>
              {afetados.length
                ? `Afeta ${plural(afetados.length, 'aluno', 'alunos')} em andamento: pode${afetados.length === 1 ? '' : 'm'} pular ou repetir conteúdo.`
                : sujo ? 'Nenhum aluno em andamento é afetado.' : 'Salve para renumerar 1, 2, 3... sem mudar a ordem.'}
            </span>
          </div>
          <div className="mc-barra-salvar__acoes">
            {sujo && <button type="button" className="btn-secondary mc-botao" onClick={descartar} disabled={salvando}>Descartar</button>}
            <button type="button" className="btn-primary mc-botao" onClick={pedirParaSalvar} disabled={salvando}>
              {salvando ? 'Salvando...' : sujo ? 'Salvar sequência' : 'Corrigir numeração'}
            </button>
          </div>
        </div>
      )}

      {adicionarAberto ? (
        <AdicionarEtapa
          pilulas={pilulas}
          questoes={questoes}
          temas={temas}
          temaCursoId={temaCursoId}
          idsNoCurso={idsNoCurso}
          proximaPosicao={ativas.length + 1}
          onAdicionar={adicionar}
          onFechar={() => setAdicionarAberto(false)}
        />
      ) : (
        <button type="button" className="btn-secondary mc-botao mc-adicionar-botao" onClick={() => setAdicionarAberto(true)}>
          <FiPlus aria-hidden="true" /> Adicionar etapa no fim
        </button>
      )}

      {confirmando && (
        <Dialogo
          titulo={removidas.size ? 'Salvar e remover etapas?' : 'Salvar a nova ordem?'}
          confirmar={removidas.size ? 'Remover e salvar' : 'Salvar mesmo assim'}
          perigo
          ocupado={salvando}
          onConfirmar={gravar}
          onFechar={() => setConfirmando(false)}
        >
          {removidas.size > 0 && (
            <>
              <p>{removidas.size === 1 ? 'Esta etapa sai do curso:' : 'Estas etapas saem do curso:'}</p>
              <ul className="mc-lista-simples">
                {todas.filter((e) => removidas.has(e.id)).map((e) => (
                  <li key={e.id}><strong>Etapa {posicaoSalva[e.id]}</strong> · {rotuloEtapa(e)}: {tituloEtapa(e)}</li>
                ))}
              </ul>
              <p className="mc-suave">A pílula ou questão continua no banco de conteúdo; só deixa de fazer parte deste curso.</p>
            </>
          )}
          {afetados.length > 0 && (
            <p className="mc-alerta mc-alerta--atencao">
              <FiAlertTriangle aria-hidden="true" />
              <span>
                <strong>{plural(afetados.length, 'aluno em andamento', 'alunos em andamento')}</strong> já
                {afetados.length === 1 ? ' recebeu' : ' receberam'} etapas que mudam de lugar ou saem do curso.
                O bot segue pela posição: depois de salvar, {afetados.length === 1 ? 'ele pode' : 'eles podem'} pular
                ou repetir conteúdo.
              </span>
            </p>
          )}
        </Dialogo>
      )}
    </div>
  );

  if (!lateral) return lista;
  return (
    <div className="mc-etapas-grade">
      {lista}
      <aside className="mc-etapas-grade__lateral" aria-label="Prévia">{lateral}</aside>
    </div>
  );
}

export default EtapasCurso;
